import { supabase } from '../supabase';
import { callWahaProxy } from '../services/wahaProxyService';
import { normalizeWhatsAppDigits } from '../services/whatsapp/phoneDigits';

// 29/10/2026: tolte le funzioni senza chiamanti (lette, digitazione, invio diretto, file,
// immagini, video, vocali, pulsanti con /api/sendButtons deprecato). Restano le automazioni
// su offerte, progetti e ticket (triggerWhatsAppAutomation) e il loro invio.

/**
 * Sends a WhatsApp notification directly via WAHA API
 *
 * @param params Object containing the notification parameters
 * @param params.phone The phone number to send the message to
 * @param params.message The message content
 * @param params.clientId Optional client ID associated with the message
 * @param params.organizationId Optional organization ID
 * @param params.configId Optional specific API configuration ID to use
 * @returns Promise resolving to the response from the API
 */
export const sendWhatsAppNotification = async (params: {
  phone: string;
  message: string;
  clientId?: string;
  organizationId?: string;
  configId?: string;
}) => {
  try {
    // Get the appropriate WhatsApp configuration
    let config;
    let userId;

    // First get the current user
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw new Error('Authentication error: ' + userError.message);
    userId = userData.user?.id;

    if (!userId) throw new Error('User not authenticated');

    // If a specific config ID is provided, use that
    if (params.configId) {
      const { data: configData, error: configError } = await supabase
        .from('api_configurations')
        .select('*')
        .eq('id', params.configId)
        .single();

      if (configError) throw new Error('Config error: ' + configError.message);
      config = configData;
    } else {
      // Otherwise, determine which configuration to use based on organization settings
      // Get user's organization
      let orgId = params.organizationId;

      if (!orgId) {
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userId)
          .single();

        if (profileData) orgId = profileData.organization_id;
      }

      if (!orgId) throw new Error('Organization ID not found');

      // Check if organization uses global or per-user WhatsApp config
      const { data: settings } = await supabase
        .from('settings')
        .select('whatsapp_global_config')
        .eq('organization_id', orgId)
        .single();

      const isGlobalMode = settings?.whatsapp_global_config !== false;

      if (isGlobalMode) {
        // Global mode: get organization-wide config
        const { data: globalConfig, error: globalConfigError } = await supabase
          .from('api_configurations')
          .select('*')
          .eq('service_name', 'waha')
          .eq('organization_id', orgId)
          .is('user_id', null)
          .single();

        if (globalConfigError) throw new Error('Global config error: ' + globalConfigError.message);
        config = globalConfig;
      } else {
        // Per-user mode: get user's assigned config
        const { data: userAccess, error: accessError } = await supabase
          .from('whatsapp_user_access')
          .select('configuration_id')
          .eq('user_id', userId)
          .eq('organization_id', orgId)
          .maybeSingle();

        if (accessError) throw new Error('User access error: ' + accessError.message);

        if (!userAccess?.configuration_id) {
          throw new Error('No WhatsApp configuration assigned to this user');
        }

        const { data: userConfig, error: configError } = await supabase
          .from('api_configurations')
          .select('*')
          .eq('id', userAccess.configuration_id)
          .single();

        if (configError) throw new Error('User config error: ' + configError.message);
        config = userConfig;
      }
    }

    if (!config) throw new Error('WhatsApp configuration not found');

    // Format phone number
    let formattedPhone = params.phone;

    // Se non e' gia' un chatId, cifre con la normalizzazione comune (39 solo ai numeri
    // italiani senza prefisso, gli esteri restano come sono) e suffisso @c.us come vuole WAHA.
    // Prima si usava @s.whatsapp.net e il 39 veniva aggiunto a qualunque numero senza "+".
    if (!formattedPhone.includes('@')) {
      formattedPhone = `${normalizeWhatsAppDigits(formattedPhone)}@c.us`;
    }

    // Make the direct call to WAHA API
    const response = await callWahaProxy({
      method: 'POST',
      path: '/api/sendText',
      body: {
        session: config.configuration.session,
        chatId: formattedPhone,
        text: params.message,
        linkPreview: true,
      },
      signal: AbortSignal.timeout(10000),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    // Log the message in the database
    const { error: logError } = await supabase
      .from('whatsapp_messages')
      .insert({
        id: crypto.randomUUID(), // Generate UUID for the id field
        from_jid: config.configuration.phone_number || config.configuration.session,
        to_jid: formattedPhone.split('@')[0], // Store without the suffix
        content: params.message,
        direction: 'outbound',
        status: 'sent',
        organization_id: config.organization_id,
        config_id: config.id,
        client_id: params.clientId
        // Rimuoviamo il campo user_id poiché non esiste nella tabella
      });

    if (logError) console.error('Error logging WhatsApp message:', logError);

    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending WhatsApp notification:', error);
    return { success: false, error: (error as Error).message };
  }
};

/**
 * Sends a WhatsApp automation trigger to check for and process any matching rules
 * 
 * @param params Object containing the trigger parameters
 * @param params.entityType The type of entity triggering the automation
 * @param params.triggerEvent The event that triggered the automation
 * @param params.entityData The entity data including all fields needed for templates
 * @param params.oldStatus Optional previous status for status_change events
 * @returns Promise resolving to the response from the Edge Function
 */
export const triggerWhatsAppAutomation = async (params: {
  entityType: 'deal' | 'project' | 'ticket' | 'lead' | 'client';
  triggerEvent: 'create' | 'update' | 'delete' | 'status_change' | 'state_transition';
  entityData: any;
  oldStatus?: string;
  oldStateId?: string; // For state_transition events
}) => {
  try {
    // Check if notifications are disabled for this entity
    if (params.entityData.send_notifications === false) {
      console.log(`WhatsApp notifications disabled for this ${params.entityType}`, params.entityData.id);
      return { success: true, data: { message: "Notifications disabled for this entity" } };
    }
    
    // First try to get the automation rules that match this event
    const { data: rules, error: rulesError } = await supabase
      .from('whatsapp_automation_rules')
      .select('*')
      .eq('entity_type', params.entityType)
      .eq('trigger_event', params.triggerEvent)
      .eq('active', true);
    
    if (rulesError) throw rulesError;
    
    if (!rules || rules.length === 0) {
      console.log(`No active automation rules found for ${params.entityType} ${params.triggerEvent}`);
      return { success: true, data: { message: "No matching rules" } };
    }
    
    // Get user's organization ID first
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('User not authenticated');

    const { data: userProfile } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', userData.user.id)
      .single();

    if (!userProfile?.organization_id) throw new Error('User not associated with organization');

    // Get the WAHA API configuration for this organization
    const { data: config, error: configError } = await supabase
      .from('api_configurations')
      .select('*')
      .eq('service_name', 'waha')
      .eq('organization_id', userProfile.organization_id)
      .is('user_id', null)  // Use organization-wide config by default
      .maybeSingle();  // Use maybeSingle to avoid error if no config found

    if (configError) throw configError;
    if (!config) throw new Error('No WhatsApp configuration found for this organization');
    
    // Process each matching rule
    for (const rule of rules) {
      // For status_change events, check if status actually changed
      if (rule.trigger_event === 'status_change') {
        // Skip if status didn't actually change
        if (params.oldStatus === params.entityData.status) {
          console.log('Status did not change, skipping rule:', rule.name);
          continue;
        }

        // Skip if trigger_status is specified and doesn't match current status
        if (rule.trigger_status && params.entityData.status !== rule.trigger_status) {
          console.log(`Status ${params.entityData.status} doesn't match required trigger status ${rule.trigger_status}, skipping rule:`, rule.name);
          continue;
        }
      }
      
      // Get recipient phone number
      let recipientPhone: string | null = null;
      
      if (rule.recipient_type === 'client' && params.entityData.client_id) {
        // Get client phone number
        const { data: client, error: clientError } = await supabase
          .from('clients')
          .select('whatsapp_number, phone')
          .eq('id', params.entityData.client_id)
          .single();
        
        if (clientError) {
          console.error('Error fetching client:', clientError);
          continue;
        }
        
        recipientPhone = client.whatsapp_number || client.phone;
      } else if (rule.recipient_type === 'assigned_user' && params.entityData.assigned_to) {
        // Get user phone number
        const { data: user, error: userError } = await supabase
          .from('user_profiles')
          .select('phone')
          .eq('id', params.entityData.assigned_to)
          .single();
        
        if (userError) {
          console.error('Error fetching user:', userError);
          continue;
        }
        
        recipientPhone = user.phone;
      }
      
      if (!recipientPhone) {
        console.error('No recipient phone number found');
        continue;
      }
      
      // Process message template variables
      let message = await processMessageTemplate(rule.message_template, params.entityType, params.entityData);
      
      // Convert HTML to WhatsApp-friendly format
      message = convertHtmlToWhatsAppFormat(message);
      
      // Send the message using our sendWhatsAppNotification function
      try {
        // Use the same sendWhatsAppNotification function that's used elsewhere
        const sendResult = await sendWhatsAppNotification({
          phone: recipientPhone,
          message: message,
          clientId: params.entityData.client_id || undefined,
          organizationId: rule.organization_id,
          configId: config.id
        });

        if (!sendResult.success) {
          throw new Error(`Failed to send WhatsApp message: ${sendResult.error}`);
        }

        console.log('WhatsApp message sent successfully:', sendResult.data);
          
      } catch (error) {
        console.error('Error sending WhatsApp message:', error);
        
        // Log the failed automation
        await supabase
          .from('whatsapp_automation_logs')
          .insert({
            rule_id: rule.id,
            entity_type: rule.entity_type,
            entity_id: params.entityData.id,
            recipient_type: rule.recipient_type,
            recipient_id: rule.recipient_type === 'client' ? params.entityData.client_id : params.entityData.assigned_to,
            message: 'Error: ' + (error as Error).message,
            status: 'failed',
            organization_id: rule.organization_id
          });
      }
    }
    
    return { success: true, data: { message: "Processed automation rules" } };
  } catch (error) {
    console.error('Error triggering WhatsApp automation:', error);
    return { success: false, error: (error as Error).message };
  }
};

// Map per tradurre gli stati in italiano
const STATUS_TRANSLATIONS = {
  // Stati deal
  'draft': 'Bozza',
  'sent': 'Inviato',
  'accepted': 'Accettato',
  'rejected': 'Rifiutato',

  // Stati comuni
  'pending': 'In attesa',
  'in_progress': 'In corso',
  'completed': 'Completata',
  'cancelled': 'Cancellata',
  'to_do': 'Da fare',
  'done': 'Completata',
  'doing': 'In corso',
  'backlog': 'In coda',
  'review': 'In revisione',
  'open': 'Aperto',
  'closed': 'Chiuso',
  'overdue': 'In ritardo',
  'not_started': 'Non iniziato',
  'waiting': 'In attesa',
  'verified': 'Verificato',
  'submitted': 'Presentato',
  'active': 'Attivo',
  'inactive': 'Inattivo',
  'expired': 'Scaduto',
  'upcoming': 'In arrivo',
  'past_due': 'Scaduto',
  'paid': 'Pagato',
  'unpaid': 'Non pagato',
  'partial': 'Parziale',

  // Stati progetto
  'planned': 'Pianificato',
  'started': 'Iniziato',
  'on_hold': 'In pausa',
  'finished': 'Terminato',
  'delayed': 'In ritardo',
  'project_cancelled': 'Annullato',

  // Stati ticket
  'new': 'Nuovo',
  'assigned': 'Assegnato',
  'resolved': 'Risolto',
  'ticket_closed': 'Chiuso',
  'reopened': 'Riaperto',
  'waiting_for_customer': 'In attesa del cliente',
  'waiting_for_third_party': 'In attesa di terzi',
  'ticket_in_progress': 'In corso',
  'high': 'Alta',
  'medium': 'Media',
  'low': 'Bassa',

  // Stati generici
  'created': 'Creato',
  'updated': 'Aggiornato',
  'deleted': 'Eliminato',
  'modified': 'Modificato',
  'inserted': 'Inserito'
};

// Funzione per tradurre uno stato in italiano
function translateStatus(status: string): string {
  // Aggiungiamo un log per vedere quali stati vengono tradotti
  console.log(`Translating status: '${status}' -> '${STATUS_TRANSLATIONS[status] || status}'`);

  // Normalizzazione: converti in minuscolo e rimuovi spazi iniziali/finali
  const normalizedStatus = status.toLowerCase().trim();

  // Prova prima con lo stato originale, poi con quello normalizzato
  return STATUS_TRANSLATIONS[status] || STATUS_TRANSLATIONS[normalizedStatus] || status;
}

// Helper function to process message templates with variables
async function processMessageTemplate(
  template: string,
  entityType: string,
  entityData: any
): Promise<string> {
  let message = template;

  // Replace entity-specific variables
  switch (entityType) {
    case 'deal':
      message = message.replace(/{{deal\.title}}/g, entityData.title || '');
      message = message.replace(/{{deal\.status}}/g, translateStatus(entityData.status || ''));
      message = message.replace(/{{deal\.amount}}/g, formatCurrency(entityData.amount));
      message = message.replace(/{{deal\.description}}/g, entityData.description || '');
      message = message.replace(/{{deal\.close_date}}/g, formatDate(entityData.close_date));
      
      // Add client name from nested client object if available
      if (entityData.client && entityData.client.business_name) {
        message = message.replace(/{{deal\.client_name}}/g, entityData.client.business_name);
      }
      
      // Add assignee name if available
      if (entityData.assignee && entityData.assignee.full_name) {
        message = message.replace(/{{deal\.assignee_name}}/g, entityData.assignee.full_name);
      }
      break;
      
    case 'project':
      message = message.replace(/{{project\.name}}/g, entityData.name || '');
      message = message.replace(/{{project\.status}}/g, translateStatus(entityData.status || ''));
      message = message.replace(/{{project\.due_date}}/g, formatDate(entityData.due_date));
      message = message.replace(/{{project\.description}}/g, entityData.description || '');
      message = message.replace(/{{project\.start_date}}/g, formatDate(entityData.start_date));
      
      // Add client name from nested client object if available
      if (entityData.client && entityData.client.business_name) {
        message = message.replace(/{{project\.client_name}}/g, entityData.client.business_name);
      }
      
      // Add assignee name if available
      if (entityData.assignee && entityData.assignee.full_name) {
        message = message.replace(/{{project\.assignee_name}}/g, entityData.assignee.full_name);
      }
      break;
      
    case 'ticket':
      message = message.replace(/{{ticket\.subject}}/g, entityData.subject || '');
      message = message.replace(/{{ticket\.status}}/g, translateStatus(entityData.status || ''));
      message = message.replace(/{{ticket\.priority}}/g, entityData.priority || '');
      message = message.replace(/{{ticket\.description}}/g, entityData.description || '');
      message = message.replace(/{{ticket\.created_at}}/g, formatDate(entityData.created_at));
      message = message.replace(/{{ticket\.due_date}}/g, formatDate(entityData.due_date));
      
      // Add client name from nested client object if available
      if (entityData.client && entityData.client.business_name) {
        message = message.replace(/{{ticket\.client_name}}/g, entityData.client.business_name);
      }
      
      // Add assignee name if available
      if (entityData.assignee && entityData.assignee.full_name) {
        message = message.replace(/{{ticket\.assignee_name}}/g, entityData.assignee.full_name);
      }
      break;
  }
  
  // Add debugging if template variables remain unfilled
  if (message.includes('{{') && message.includes('}}')) {
    console.log('Template variables not fully replaced. Debug entity data:', JSON.stringify(entityData, null, 2));
  }

  // Get client data if needed
  if (message.includes('{{client.') && entityData.client_id) {
    const { data: client, error: clientError } = await supabase
      .from('clients')
      .select('*')
      .eq('id', entityData.client_id)
      .single();

    if (!clientError && client) {
      message = message.replace(/{{client\.name}}/g, client.business_name || '');
      // Add more client fields as needed
    }
  }

  // Get user data if needed
  if (message.includes('{{user.') && entityData.assigned_to) {
    const { data: user, error: userError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', entityData.assigned_to)
      .single();

    if (!userError && user) {
      message = message.replace(/{{user\.name}}/g, user.full_name || user.email || '');
      // Add more user fields as needed
    }
  }

  // Template variables are handled within entity-specific processing above
  // No generic template processing needed for current entity types

  return message;
}

function formatDate(dateString?: string): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
}

function formatCurrency(amount?: number): string {
  if (amount === undefined || amount === null) return '';
  return `€${amount.toFixed(2)}`;
}

/**
 * Converts HTML formatted text to WhatsApp-friendly text format
 * Handles common HTML tags and converts them to appropriate WhatsApp formatting
 * 
 * @param htmlText The HTML text to convert
 * @returns Plain text with WhatsApp-compatible formatting
 */
function convertHtmlToWhatsAppFormat(htmlText: string): string {
  if (!htmlText) return '';
  
  let text = htmlText;
  
  // Replace common HTML tags with WhatsApp formatting
  
  // Replace paragraph breaks with double newlines
  text = text.replace(/<\/p>\s*<p>/gi, '\n\n');
  
  // Replace single breaks with newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');
  
  // Replace bold tags with asterisks
  text = text.replace(/<strong>(.*?)<\/strong>/gi, '*$1*');
  text = text.replace(/<b>(.*?)<\/b>/gi, '*$1*');
  
  // Replace italic tags with underscores
  text = text.replace(/<em>(.*?)<\/em>/gi, '_$1_');
  text = text.replace(/<i>(.*?)<\/i>/gi, '_$1_');
  
  // Replace strikethrough
  text = text.replace(/<strike>(.*?)<\/strike>/gi, '~$1~');
  text = text.replace(/<s>(.*?)<\/s>/gi, '~$1~');
  
  // Replace lists
  text = text.replace(/<li>(.*?)<\/li>/gi, '• $1\n');
  
  // Clean up any remaining tags
  text = text.replace(/<[^>]*>/g, '');
  
  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#39;/g, "'");
  
  // Trim extra whitespace
  text = text.replace(/\n\s*\n\s*\n/g, '\n\n');
  text = text.trim();
  
  return text;
}
